// frontend/src/components/Layout/HeroCampaign.jsx
// VESHH-EON — Campaign hero
//
// Large screens (md and up) get HERO_L, phones get HERO_S, via <picture>.
// The text overlay (eyebrow, headline, CTA) sits on top of the image.
// No libraries, no JS, no animation. The browser downloads only one of the two images.
//
// <HeroCampaign />              text on the left (desktop)
// <HeroCampaign side="right" /> text on the right (desktop)

import React from "react";

const BASE =
  "https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/f_auto/q_auto";
const LARGE_ID = "HERO_L_v3ybpv.png";
const SMALL_ID = "HERO_S_e3e9lr.png";
const ALL_URL = "/collections/all";

const url = (id) => `${BASE}/${id}`;
// c_limit never upscales; the browser picks the smallest width that fits the screen
const srcSet = (id, widths) =>
  widths.map((w) => `${BASE}/c_limit,w_${w}/${id} ${w}w`).join(", ");

const HeroCampaign = ({ side = "left" }) => {
  const right = side === "right";

  return (
    <section className="relative w-full overflow-hidden bg-[#f8f8f8] text-[#0A0A0A]">
      {/* Mobile: 3:4 frame. Desktop: fills the screen, image crops from the centre. */}
      <div className="relative aspect-[3/4] max-h-[100svh] w-full md:aspect-auto md:h-[100svh] md:max-h-[1000px] md:min-h-[600px]">
        <picture>
          <source
            media="(min-width: 768px)"
            srcSet={srcSet(LARGE_ID, [1280, 1920, 2560])}
            sizes="100vw"
          />
          <img
            src={url(SMALL_ID)}
            srcSet={srcSet(SMALL_ID, [480, 768, 1080])}
            sizes="100vw"
            alt="VESHH-EON models wearing the collection in a minimalist showroom"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </picture>

        {/* Readability scrims: fade into the page colour so the text always has calm ground */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#f8f8f8] via-[#f8f8f8]/75 to-transparent md:hidden"
          aria-hidden="true"
        />
        <div
          className={`pointer-events-none absolute inset-y-0 hidden w-[50%] md:block ${
            right
              ? "right-0 bg-gradient-to-l from-[#f8f8f8]/85 via-[#f8f8f8]/45 to-transparent"
              : "left-0 bg-gradient-to-r from-[#f8f8f8]/85 via-[#f8f8f8]/45 to-transparent"
          }`}
          aria-hidden="true"
        />

        {/* ── Text overlay ── */}
        <div
          className={`absolute inset-x-0 bottom-0 flex flex-col px-5 pb-7 md:inset-x-auto md:inset-y-0 md:w-[40%] md:justify-center md:px-0 md:pb-0 ${
            right ? "md:right-0 md:pr-[5vw]" : "md:left-0 md:pl-[5vw]"
          }`}
        >
          <p className="mb-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-zinc-600 md:mb-5 md:text-xs">
            <span className="h-px w-8 bg-black/60 md:w-12" aria-hidden="true" />
            The Drop
          </p>

          <h1 className="text-[clamp(1.75rem,8.5vw,2.5rem)] font-black uppercase leading-[0.98] tracking-tight md:text-[clamp(2rem,3.8vw,3.75rem)]">
            Walk like you own
            <br />
            the damn street.
          </h1>

          <a
            href={ALL_URL}
            className="mt-5 inline-flex w-full items-center justify-center bg-black px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-zinc-800 active:bg-zinc-700 md:mt-8 md:w-auto md:self-start"
          >
            Shop all
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroCampaign;
